import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon, XIcon } from 'components/icons';
import { useDateRangeModal } from 'components/modals/date-range/useDateRangeModal';
import { Checkbox } from 'components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from 'components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tooltip } from 'components/ui/tooltip';
import { addMonths, format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations';
import {
    DateRangeFilterForm,
    FilterCheckboxInForm,
    MainFilterForm,
    PeriodQuickFilter,
    QuickFilterCheckboxGroupForm,
    QuickFilterDropdownMultiselectForm,
    QuickFilterForm,
    SelectOption,
} from '../ListTableTypes';
import {
    isDropdownMultiselect,
    isGroupDisabled,
    QuickFilterChangeHandler,
    toggleQuickFilterOption,
    useClearDisabledQuickFilters,
} from '../filter-pieces/quickFilterToggle';
import { MonthPicker } from '../filter-pieces/inputs/MonthPicker';
import { isPickableMonth, monthOfPeriod, monthRange } from '../monthPeriod';
import { dayOf, periodFilterOf, presetOf } from '../periodPreset';
import { isRuledOut } from '../ruledOutOptions';

type Props = {
    quickFilters: QuickFilterForm;
    handleChange: QuickFilterChangeHandler;
    handleDropdownChange?: (selected: SelectOption[], selector: string, comperator?: string) => void;
    mainFilterState: MainFilterForm;
    handleMainFilterStateChange: (state: MainFilterForm) => void;
    /** The period chip, drawn first; it needs `applyMainFilters`. */
    period?: PeriodQuickFilter;
    /** Takes a whole new main filter state and fetches with it. */
    applyMainFilters?: (state: MainFilterForm) => void;
};

/** A dropdown with more options than this offers a search box over them. */
const SEARCHABLE_OPTION_COUNT = 8;

/**
 * The new design's quick filters: one chip per quick filter, in the order the list configures them. A chip
 * never moves when it turns on, it only changes state. A single option toggles on a click; a group of options
 * opens a list of them, and its × clears it without opening anything. A group that takes several options lists them
 * with checkboxes, applied as each is ticked; a single-choice group lists its answers as plain rows, and a pick
 * applies and closes the list.
 */
export default function QuickFilterChips({
    quickFilters,
    handleChange,
    handleDropdownChange,
    mainFilterState,
    handleMainFilterStateChange,
    period,
    applyMainFilters,
}: Props) {
    useClearDisabledQuickFilters(quickFilters, mainFilterState, handleChange);

    const entries = Object.entries(quickFilters).filter(
        ([, filter]) => isDropdownMultiselect(filter) || answersOf(filter, mainFilterState).length > 0
    );
    const periodEntry = period ? periodFilterOf(mainFilterState, period.selector) : undefined;
    if (!entries.length && !periodEntry) return null;

    return (
        // The chips are items of the filter bar's own wrapping row. On a narrow screen they take a line of their own
        // that scrolls sideways; the option lists portal out of it. Inline-size containment keeps that line's width
        // out of the ancestors' min-content size, which would otherwise widen the list's chrome past the screen.
        <div
            data-slot="quick-filters"
            className="contents [scrollbar-width:none] max-sm:flex max-sm:basis-full max-sm:items-center max-sm:gap-1.5 max-sm:overflow-x-auto max-sm:[contain:inline-size]"
        >
            {period?.month && periodEntry && applyMainFilters ? (
                <MonthChip
                    filter={periodEntry[1]}
                    onApply={(filter) => applyMainFilters({ ...mainFilterState, [periodEntry[0]]: filter })}
                />
            ) : period && periodEntry && applyMainFilters ? (
                <PeriodChip
                    period={period}
                    filter={periodEntry[1]}
                    onApply={(filter) => applyMainFilters({ ...mainFilterState, [periodEntry[0]]: filter })}
                />
            ) : null}
            {entries.map(([key, filter]) => {
                if (isDropdownMultiselect(filter)) {
                    return (
                        <DropdownChip
                            key={key}
                            filter={filter}
                            onChange={(selected) =>
                                handleDropdownChange?.(selected, filter.selector, filter.comperator)
                            }
                        />
                    );
                }
                const toggle = (option: FilterCheckboxInForm, checked: boolean, immediateFetch?: boolean) =>
                    toggleQuickFilterOption({
                        group: filter,
                        option,
                        checked,
                        handleChange,
                        mainFilterState,
                        handleMainFilterStateChange,
                        immediateFetch,
                    });
                const isDisabled = isGroupDisabled(filter, mainFilterState);
                const answers = answersOf(filter, mainFilterState);
                return answers.length === 1 ? (
                    <ToggleChip
                        key={key}
                        option={answers[0]}
                        isDisabled={isDisabled}
                        onToggle={(checked) => toggle(answers[0], checked)}
                    />
                ) : (
                    <GroupChip key={key} group={filter} answers={answers} isDisabled={isDisabled} onToggle={toggle} />
                );
            })}
        </div>
    );
}

/** The options a checkbox group draws; a group's `no` answer and an option the filters rule out are never among them. */
function answersOf(group: QuickFilterCheckboxGroupForm, mainFilterState: MainFilterForm): FilterCheckboxInForm[] {
    return (group.filters ?? []).filter((option) => !option.isNo && !isRuledOut(option, mainFilterState));
}

/** The chip's name while nothing is picked: the group's label, else its chip label, else its options. */
function groupName(group: QuickFilterCheckboxGroupForm, answers: FilterCheckboxInForm[]): string {
    if (group.label) return t(group.label);
    if (group.chipLabel) return t(group.chipLabel);
    return answers.map((option) => option.label).join(' / ');
}

/**
 * One option picked reads as that option, or as the group's name and the option when `withName` is set; several
 * as the group's name and how many. Read with the name, the answer is lower case and a name phrased as a question
 * drops its question mark: `Is active: yes`.
 */
function pickedLabel(name: string, picked: string[], withName = false): string {
    if (!picked.length) return name;
    if (picked.length > 1) return t('filters.chip-count', { label: name, count: String(picked.length) });
    return withName
        ? t('filters.chip-answer', { label: name.replace(/\?$/, ''), answer: picked[0].toLocaleLowerCase() })
        : picked[0];
}

/**
 * A chip one click switches, a quick filter's single option or a yes / no filter: a 44px tap area under a coarse
 * pointer, and the press, off while the chip is disabled.
 */
export const TOGGLE_CHIP =
    'hit-area relative cursor-pointer px-2.5 transition-[scale] duration-(--press-dur) ease-out motion-safe:not-disabled:will-change-transform motion-safe:not-disabled:active:scale-(--press-scale)';

export function chipClassName({ isOn }: { isOn: boolean }) {
    return cn(
        'text-meta new:select-none inline-flex h-8 shrink-0 items-center rounded-full border whitespace-nowrap',
        isOn
            ? 'bg-primary-tint border-primary text-primary-ink font-semibold'
            : 'bg-paper border-border-control text-foreground-secondary hover:bg-surface-subtle'
    );
}

function ToggleChip({
    option,
    isDisabled,
    onToggle,
}: {
    option: FilterCheckboxInForm;
    isDisabled: boolean;
    onToggle: (checked: boolean) => void;
}) {
    const isOn = option.isSelected;
    const chip = (
        <button
            type="button"
            aria-pressed={isOn}
            disabled={isDisabled}
            onClick={() => onToggle(!isOn)}
            className={cn(chipClassName({ isOn }), TOGGLE_CHIP, 'disabled:cursor-not-allowed disabled:opacity-50')}
        >
            {option.label}
        </button>
    );
    if (!option.tooltip) return chip;
    return <Tooltip content={<FormattedMessage id={option.tooltip} values={option.tooltipValues} />}>{chip}</Tooltip>;
}

function GroupChip({
    group,
    answers,
    isDisabled,
    onToggle,
}: {
    group: QuickFilterCheckboxGroupForm;
    answers: FilterCheckboxInForm[];
    isDisabled: boolean;
    onToggle: (option: FilterCheckboxInForm, checked: boolean, immediateFetch?: boolean) => void;
}) {
    const name = groupName(group, answers);
    const picked = answers.filter((option) => option.isSelected);
    const clear = () => picked.forEach((option, index) => onToggle(option, false, index === picked.length - 1));
    const tooltipOf = (option: FilterCheckboxInForm) =>
        option.tooltip ? <FormattedMessage id={option.tooltip} values={option.tooltipValues} /> : undefined;
    return (
        <ChipWithList
            name={name}
            label={pickedLabel(
                name,
                picked.map((option) => option.label),
                group.answerWithLabel
            )}
            isOn={picked.length > 0}
            isDisabled={isDisabled}
            onClear={clear}
            isNarrow={group.multiple !== true}
        >
            {(close) =>
                group.multiple === true ? (
                    <div role="group" aria-label={name} className="flex flex-col">
                        {answers.map((option) => (
                            <OptionRow
                                key={option.key}
                                label={option.label}
                                tooltip={tooltipOf(option)}
                                checked={option.isSelected}
                                onChange={(checked) => onToggle(option, checked)}
                            />
                        ))}
                    </div>
                ) : (
                    <div role="radiogroup" aria-label={name} className="flex flex-col">
                        {answers.map((option) => (
                            <AnswerRow
                                key={option.key}
                                label={option.label}
                                tooltip={tooltipOf(option)}
                                checked={option.isSelected}
                                onPick={() => {
                                    if (!option.isSelected) onToggle(option, true);
                                    close();
                                }}
                            />
                        ))}
                    </div>
                )
            }
        </ChipWithList>
    );
}

function DropdownChip({
    filter,
    onChange,
}: {
    filter: QuickFilterDropdownMultiselectForm;
    onChange: (selected: SelectOption[]) => void;
}) {
    const isPicked = (option: SelectOption) => filter.selected.some((chosen) => chosen.value === option.value);
    return (
        <ChipWithList
            name={filter.label}
            label={pickedLabel(
                filter.label,
                filter.selected.map((option) => option.label)
            )}
            isOn={filter.selected.length > 0}
            onClear={() => onChange([])}
        >
            <DropdownOptions
                label={filter.label}
                options={filter.options}
                isPicked={isPicked}
                onToggle={(option, checked) =>
                    onChange(
                        checked
                            ? [...filter.selected, option]
                            : filter.selected.filter((chosen) => chosen.value !== option.value)
                    )
                }
            />
        </ChipWithList>
    );
}

function DropdownOptions({
    label,
    options,
    isPicked,
    onToggle,
}: {
    label: string;
    options: SelectOption[];
    isPicked: (option: SelectOption) => boolean;
    onToggle: (option: SelectOption, checked: boolean) => void;
}) {
    const [query, setQuery] = useState('');
    const needle = foldForSearch(query);
    const shown = needle ? options.filter((option) => foldForSearch(option.label).includes(needle)) : options;
    const searchLabel = t('search-records', { record: label.toLocaleLowerCase() });
    return (
        <div className="flex min-h-0 flex-col gap-1">
            {options.length > SEARCHABLE_OPTION_COUNT ? (
                <InputGroup className="bg-paper border-border-control h-[34px] shrink-0 shadow-none">
                    <InputGroupAddon align="inline-start">
                        <SearchIcon className="size-3.5" />
                    </InputGroupAddon>
                    <InputGroupInput
                        placeholder={searchLabel}
                        aria-label={searchLabel}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                </InputGroup>
            ) : null}
            <div role="group" aria-label={label} className="flex max-h-80 flex-col overflow-y-auto overscroll-contain">
                {shown.map((option) => (
                    <OptionRow
                        key={String(option.value)}
                        label={option.label}
                        checked={isPicked(option)}
                        onChange={(checked) => onToggle(option, checked)}
                    />
                ))}
                {shown.length === 0 ? (
                    <p className="text-muted-foreground text-meta px-2 py-2">{t('no-options')}</p>
                ) : null}
            </div>
        </div>
    );
}

const CUSTOM_PERIOD = 'custom';

/**
 * The period quick filter, as the dashboard picks its period: a preset applies on a pick, Custom date range opens
 * the date range dialog. The chip reads as the preset the range matches, else as the range itself; while a range is
 * set it is on, and its × empties it. A list with a default preset starts from it again when it next opens.
 */
function PeriodChip({
    period,
    filter,
    onApply,
}: {
    period: PeriodQuickFilter;
    filter: DateRangeFilterForm;
    onApply: (filter: DateRangeFilterForm) => void;
}) {
    const dateFormat = useEffectiveDateFormat();
    const today = new Date();
    const from = dayOf(filter.from);
    const to = dayOf(filter.to);
    const isSet = from !== undefined || to !== undefined;
    const picked = presetOf(period, filter, today);
    const name = filter.label ?? t('time.custom-date-range');
    const shown = (day: string | undefined) => (day ? format(parseISO(day), dateFormat) : '');
    const apply = (range: { from: Date | null; to: Date | null }) =>
        onApply({
            ...filter,
            from: range.from?.toISOString() ?? null,
            to: range.to?.toISOString() ?? null,
            isEmpty: false,
        });
    const { handleSelectChange } = useDateRangeModal({
        onDateRangeSelected: (_option, dateFrom, dateTo) =>
            apply({ from: dateFrom ? parseISO(dateFrom) : null, to: dateTo ? parseISO(dateTo) : null }),
        initialDateFrom: from,
        initialDateTo: to,
    });
    const label = picked ? picked.label : isSet ? `${shown(from)}–${shown(to)}` : name;
    return (
        <ChipWithList name={name} label={label} isOn={isSet} onClear={() => apply({ from: null, to: null })} isNarrow>
            {(close) => (
                <div role="radiogroup" aria-label={name} className="flex flex-col">
                    {period.presets.map((preset) => (
                        <AnswerRow
                            key={preset.value}
                            label={preset.label}
                            checked={preset === picked}
                            onPick={() => {
                                if (preset !== picked) apply(preset.range(today));
                                close();
                            }}
                        />
                    ))}
                    <AnswerRow
                        label={t('time.custom-date-range')}
                        checked={isSet && !picked}
                        onPick={() => {
                            close();
                            handleSelectChange({ label: t('time.custom-date-range'), value: CUSTOM_PERIOD });
                        }}
                    />
                </div>
            )}
        </ChipWithList>
    );
}

/**
 * The month quick filter: the month and year, ‹ and › stepping a month back or on across years, and a click opening
 * the month picker, which applies a pick and closes. No month after today's: › is off on the current month.
 */
function MonthChip({
    filter,
    onApply,
}: {
    filter: DateRangeFilterForm;
    onApply: (filter: DateRangeFilterForm) => void;
}) {
    const [open, setOpen] = useState(false);
    const today = new Date();
    const month = monthOfPeriod(filter.from, today);
    const next = addMonths(month, 1);
    const apply = (picked: Date) => {
        const range = monthRange(picked);
        onApply({ ...filter, from: range.from.toISOString(), to: range.to.toISOString(), isEmpty: false });
    };
    const step =
        'hit-area relative flex size-6 cursor-pointer items-center justify-center rounded-full hover:bg-primary-ink/10 disabled:cursor-not-allowed disabled:opacity-40';
    return (
        <span className={cn(chipClassName({ isOn: true }), 'gap-0.5 px-1')}>
            <button
                type="button"
                aria-label={t('time.previous-month')}
                onClick={() => apply(addMonths(month, -1))}
                className={step}
            >
                <ChevronLeftIcon className="size-3.5" />
            </button>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger className="hit-area relative cursor-pointer px-1 tabular-nums">
                    {format(month, 'MMMM yyyy')}
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    positionerClassName="z-(--z-dropdown)"
                    className="w-auto gap-0 rounded-[12px] p-2"
                >
                    {open ? (
                        <MonthPicker
                            month={month}
                            onPick={(picked) => {
                                apply(picked);
                                setOpen(false);
                            }}
                        />
                    ) : null}
                </PopoverContent>
            </Popover>
            <button
                type="button"
                aria-label={t('time.next-month')}
                disabled={!isPickableMonth(next, today)}
                onClick={() => apply(next)}
                className={step}
            >
                <ChevronRightIcon className="size-3.5" />
            </button>
        </span>
    );
}

/**
 * A chip that opens a list of options under it. Off, the caret opens the list as the label does; on,
 * the chip names what is picked and its × clears that.
 */
function ChipWithList({
    name,
    label,
    isOn,
    isDisabled = false,
    onClear,
    isNarrow = false,
    children,
}: {
    name: string;
    label: string;
    isOn: boolean;
    isDisabled?: boolean;
    onClear: () => void;
    /** A list of a few short answers, sized to them rather than to the 208px a list of options starts at. */
    isNarrow?: boolean;
    /** The list, mounted only while it is open, so it opens afresh each time; a function gets what closes it. */
    children: ReactNode | ((close: () => void) => ReactNode);
}) {
    const [open, setOpen] = useState(false);
    return (
        // × sits a gap off the trigger, not in its padding, so its area meets the trigger's; on touch the gap widens.
        <span
            className={cn(chipClassName({ isOn }), isOn && 'gap-1 pointer-coarse:gap-2.5', isDisabled && 'opacity-50')}
        >
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                    disabled={isDisabled}
                    className={cn(
                        'hit-area relative flex h-full items-center gap-1 rounded-full disabled:cursor-not-allowed',
                        isOn ? 'ps-2.5' : 'px-2.5'
                    )}
                >
                    <span className="max-w-60 truncate">{label}</span>
                    {isOn ? null : <ChevronDownIcon className="size-3.5 opacity-60" />}
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    // A dropdown, stacked with the others above page chrome.
                    positionerClassName="z-(--z-dropdown)"
                    className={cn(
                        'w-auto max-w-[min(320px,calc(100vw-32px))] gap-0 rounded-[12px] p-1',
                        isNarrow ? 'min-w-32' : 'min-w-52'
                    )}
                >
                    {open ? (typeof children === 'function' ? children(() => setOpen(false)) : children) : null}
                </PopoverContent>
            </Popover>
            {isOn ? (
                <button
                    type="button"
                    aria-label={`${t('remove')} ${name}`}
                    disabled={isDisabled}
                    onClick={onClear}
                    className="hover:bg-primary-ink/10 hit-area relative me-1 flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full disabled:cursor-not-allowed"
                >
                    <XIcon className="size-3" strokeWidth={2} />
                </button>
            ) : null}
        </span>
    );
}

function OptionRow({
    label,
    tooltip,
    checked,
    onChange,
}: {
    label: string;
    tooltip?: ReactNode;
    checked: boolean;
    onChange: (checked: boolean) => void;
}) {
    const row = (
        <label className="hover:bg-surface-subtle text-meta flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md px-2 whitespace-nowrap select-none">
            <Checkbox checked={checked} onCheckedChange={onChange} />
            <span className="min-w-0 truncate">{label}</span>
        </label>
    );
    return tooltip ? (
        <Tooltip content={tooltip} side="right">
            {row}
        </Tooltip>
    ) : (
        row
    );
}

/** One answer of a single-choice group: a row with no checkbox, as its answers exclude each other; the picked one is ticked. */
function AnswerRow({
    label,
    tooltip,
    checked,
    onPick,
}: {
    label: string;
    tooltip?: ReactNode;
    checked: boolean;
    onPick: () => void;
}) {
    const row = (
        <button
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={onPick}
            className={cn(
                'hover:bg-surface-subtle focus-visible:bg-surface-subtle text-meta flex min-h-9 cursor-pointer items-center justify-between gap-2.5 rounded-md px-2 text-start whitespace-nowrap outline-none select-none',
                checked && 'text-primary-ink font-semibold'
            )}
        >
            <span className="min-w-0 truncate">{label}</span>
            {checked ? <CheckIcon className="size-3.5 shrink-0" strokeWidth={2} /> : null}
        </button>
    );
    return tooltip ? (
        <Tooltip content={tooltip} side="right">
            {row}
        </Tooltip>
    ) : (
        row
    );
}
